import {
  AspectRatio,
  Flex,
  Heading,
  Spacer,
  Stack,
  Tag,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { DateTime } from 'luxon'
import Image from 'next/legacy/image'
import { useCallback } from 'react'

import getImageLoader from '@/utils/getImageLoader'
import logger from '@/utils/logger'

import {
  BLOG_ARTICLE_CATEGORY_TO_LABEL,
  type BlogArticleDetails,
  type BlogArticlePinnedTo,
  DEFAULT_BLOG_IMAGE_URL,
} from '@/client/services/blog'
import { getBlogUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import DiagonalAvatars from '@/components/Worlds/UI/Avatar/DiagonalAvatars'

const DEFAULT_SIZE = 480

export default function BlogArticleCard({
  blogArticle,
  pinnedToContext,
}: {
  blogArticle: BlogArticleDetails
  pinnedToContext?: BlogArticlePinnedTo
}): React.ReactElement {
  const { title, category, excerpt, imageUrl, slug, authors, pinnedTo, publishedAt } = blogArticle

  const imageSrc = imageUrl ?? DEFAULT_BLOG_IMAGE_URL

  const onImageError = useCallback(() => {
    logger.error('CloudFlare image loader error', { src: imageSrc })
  }, [imageSrc])

  // We want the image to be full width
  const sizeIfPinned =
    useBreakpointValue({
      base: 480,
      md: 768,
      lg: 992,
    }) ?? DEFAULT_SIZE

  const size = pinnedToContext == null ? DEFAULT_SIZE : sizeIfPinned
  const imageLoader = getImageLoader({ size })

  return (
    <ShwLink href={getBlogUrl(slug)} alignSelf="stretch" borderRadius="8px">
      <Flex
        direction="column"
        bg="bg"
        border="2px solid"
        borderColor="border.emphasized"
        borderRadius="8px"
        position="relative"
        _hover={{ borderColor: 'primary.border' }}
        alignSelf="stretch"
        height="100%"
      >
        <AspectRatio ratio={2 / 1}>
          <Image
            layout="fill"
            alt="Blog image"
            src={imageSrc}
            loader={imageLoader}
            onError={onImageError}
          />
        </AspectRatio>
        <Stack p="16px" direction="column" spacing="12px">
          <Text variant="eyebrow">{BLOG_ARTICLE_CATEGORY_TO_LABEL[category]}</Text>
          <Heading size="2xl">{title}</Heading>
          <Text>{excerpt}</Text>
        </Stack>
        <Spacer />
        <Stack direction="row" spacing="0px" align="center" mt="12px" p="16px">
          {authors.length === 0 ? null : (
            <Stack direction="row" align="center">
              {authors.length === 1 ? (
                <Avatar user={authors[0]} size="xs" />
              ) : (
                <DiagonalAvatars userA={authors[0]} userB={authors[1]} size="xs" />
              )}
              <Text color="fg.subtle" fontSize="small">
                {authors[0].displayName}
                {authors.length > 1 ? <> and {authors[1].displayName}</> : null}
                {publishedAt == null ? null : (
                  <> on {publishedAt.toLocaleString(DateTime.DATE_FULL)}</>
                )}
              </Text>
            </Stack>
          )}
        </Stack>
        {pinnedToContext === pinnedTo ? (
          <Tag colorScheme="blue" position="absolute" top="12px" right="12px">
            Featured
          </Tag>
        ) : null}
      </Flex>
    </ShwLink>
  )
}
